import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';

import { useConfiguration } from '../../hooks';
import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import Text from '../Text';
import NoValue from '../NoValue/NoValue';

import { formatDuration } from './utils';

export interface DurationDisplayProps extends BaseProps {
  /** Number of milliseconds value for controlled input. */
  value?: number;
  /**
   * Determines number of units to be displayed.
   * @default 4
   */
  significantUnits?: 5 | 4 | 3 | 2 | 1;
  /**
   * Unit formatting style
   * @default "narrow"
   */
  unitDisplay?: Intl.NumberFormatOptions['unitDisplay'];
  /** Unit of maximum precision */
  maxPrecision?: 'day' | 'hour' | 'minute' | 'second' | 'millisecond';
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLSpanElement>;
}

const DurationDisplay: ForwardRefForwardPropsComponent<DurationDisplayProps> = forwardRef(
  function DurationDisplay(
    {
      value,
      significantUnits = 4,
      unitDisplay = 'narrow',
      maxPrecision,
      ...restProps
    }: PropsWithoutRef<DurationDisplayProps>,
    ref: DurationDisplayProps['ref']
  ) {
    const { locale } = useConfiguration();

    return (
      <Text ref={ref} {...restProps}>
        {typeof value === 'number' ? (
          formatDuration(value, { locale, significantUnits, unitDisplay, maxPrecision })
        ) : (
          <NoValue />
        )}
      </Text>
    );
  }
);

export default DurationDisplay;
